import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setBookingFormValue } from "../data/actions/bookingForm";
import {
  setAlternativeCurrencyPaymentPrice,
  setPaymentPrice,
} from "../data/actions/price";
import { AppState } from "../data/reducers";
import { toArray } from "../lib/functions";
import { insertToArrayOnIndex } from "../lib/helperFunctions";
import { getBookingFormElementDescription } from "./bookingForm/getBookingFormElementDescription";

export function useBookingFormPayment(intl, fullPrice) {
  const {
    paymentFormMethods,
    alternativeCurrencyPaymentFormMethods,
    paymentFormMethodForms,
    paymentTypeId,
    lastPaymentDate,
    bookingFormSections,
    bookingFormFields,
    defaultCountry,
    backgroundImageUrl,
  } = useSelector((state: AppState) => ({
    paymentFormMethods: state.bookingForm.paymentFormMethods,
    alternativeCurrencyPaymentFormMethods:
      state.bookingForm.alternativeCurrencyPaymentFormMethods,
    paymentFormMethodForms: state.bookingForm.paymentFormMethodForms,
    paymentTypeId: state.bookingForm.paymentTypeId,
    lastPaymentDate: state.bookingForm.lastPaymentDate,
    bookingFormSections: state.bookingForm.bookingFormSections,
    bookingFormFields: state.bookingForm.bookingFormFields,
    defaultCountry:
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      "",
    backgroundImageUrl:
      state.storage.frontendSettings?.dealerFrontendSettings?.background?.image
        ?.url,
  }));

  const dispatch = useDispatch();

  const onSelectPayment = (id) => {
    changePaymentTypePrice(id);
    dispatch(setBookingFormValue("paymentTypeId", id));
  };

  function changePaymentTypePrice(id) {
    const paymentFormMethod = paymentFormMethods?.find(
      (method) => method.Id === id
    );

    const alternativeCurrencyPaymentFormMethod = alternativeCurrencyPaymentFormMethods?.find(
      (method) => method.Id === id
    );

    if (paymentFormMethod && paymentFormMethod.Price) {
      dispatch(setPaymentPrice(paymentFormMethod.Price));
    }

    if (
      alternativeCurrencyPaymentFormMethod &&
      alternativeCurrencyPaymentFormMethod.Price
    ) {
      dispatch(
        setAlternativeCurrencyPaymentPrice(
          alternativeCurrencyPaymentFormMethod.Price
        )
      );
    }
  }

  useEffect(() => {
    if (paymentTypeId === null) {
      return;
    }

    const {
      bookingFormSectionsWithAdditionalPaymentData,
      addedFields,
    } = getBookingFormSectionsWithAdditionalPaymentData({
      bookingFormSections,
      paymentTypeId,
      paymentFormMethods,
      paymentFormMethodForms,
      intl,
    });

    dispatch(
      setBookingFormValue("bookingFormFields", {
        ...bookingFormFields,
        ...addedFields,
      })
    );
    dispatch(
      setBookingFormValue(
        "bookingFormSections",
        bookingFormSectionsWithAdditionalPaymentData
      )
    );

    changePaymentTypePrice(paymentTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTypeId, fullPrice]);

  return {
    paymentFormMethods,
    alternativeCurrencyPaymentFormMethods,
    paymentTypeId,
    onSelectPayment,
    lastPaymentDate,
    defaultCountry,
    backgroundImageUrl,
  };
}

function getBookingFormSectionsWithAdditionalPaymentData({
  bookingFormSections,
  paymentTypeId,
  paymentFormMethods,
  paymentFormMethodForms,
  intl,
}) {
  if (!paymentFormMethodForms?.length > 0 || paymentTypeId === null) {
    return {
      bookingFormSectionsWithAdditionalPaymentData: bookingFormSections,
      addedFields: {},
    };
  }

  const selectedPaymentMethod = paymentFormMethods.find(
    (paymentMethod) => paymentMethod.Id === paymentTypeId
  )?.Method;

  if (!selectedPaymentMethod) {
    return {
      bookingFormSectionsWithAdditionalPaymentData: bookingFormSections,
      addedFields: {},
    };
  }

  const applicablePaymentMethodForm = paymentFormMethodForms.find(
    (paymentMethodForm) => paymentMethodForm.Method === selectedPaymentMethod
  );

  if (!applicablePaymentMethodForm) {
    return {
      bookingFormSectionsWithAdditionalPaymentData: bookingFormSections.filter(
        (section) => section.code !== "payment-additional"
      ),
      addedFields: {},
    };
  }

  const newFieldsBasic = toArray(
    applicablePaymentMethodForm.BookingFormGroup[0]?.BookingFormElement
  ).map(
    (e) =>
      getBookingFormElementDescription({
        bookingFormElement: e,
        passengerIndex: 0,
        cbt: {},
      }) || []
  );

  const newFields = {};
  newFieldsBasic.forEach((e) => {
    newFields[e.name] = e;
  });

  const extraElements =
    toArray(
      applicablePaymentMethodForm?.BookingFormGroup[0]?.BookingFormElement
    )?.map((e) => e.Name) || [];

  const newBookingFormSectionAdditionalPaymentInformation = {
    title: intl.formatMessage({
      id: "FormBuilder.additionalPaymentInformation",
    }),
    code: "payment-additional",
    collapsible: false,
    isCollapsed: false,
    elements: extraElements || [],
  };

  const bookingFormSectionsWithoutPaymentAdditional = bookingFormSections.filter(
    (section) => section.code !== "payment-additional"
  );

  return {
    bookingFormSectionsWithAdditionalPaymentData: insertToArrayOnIndex(
      bookingFormSectionsWithoutPaymentAdditional,
      bookingFormSectionsWithoutPaymentAdditional.length - 1,
      newBookingFormSectionAdditionalPaymentInformation
    ),
    addedFields: newFields,
  };
}
